// src/components/RdoCalendario.jsx
import React, { useState, useEffect, useMemo, useRef } from "react";
import { supabase } from "../supabaseClient";
import "./RdoCalendario.css";
import { RdoCalendarioSkeleton } from "./skeletons/RdoColunaSkeleton"; // NOVO

const getDataLimiteFutura = () => {
  const amanha = new Date();
  amanha.setDate(amanha.getDate() + 1);
  amanha.setHours(0, 0, 0, 0);
  const ano = amanha.getFullYear();
  const mes = String(amanha.getMonth() + 1).padStart(2, "0");
  const dia = String(amanha.getDate()).padStart(2, "0");
  return `${ano}-${mes}-${dia}`;
};

export default function CalendarioDiarioObra({
  pilhaId,
  usuarioId,
  onSelectNota,
  onMesChange,
}) {
  const [notasDoMes, setNotasDoMes] = useState(new Map());
  const [diasPreenchidos, setDiasPreenchidos] = useState(new Set());
  const [diasEspeciais, setDiasEspeciais] = useState(new Set());
  const [mesAno, setMesAno] = useState(new Date());
  const [criandoNota, setCriandoNota] = useState(false);
  const [carregandoCalendario, setCarregandoCalendario] = useState(true); // NOVO

  const ultimoMesNotificado = useRef(null);

  useEffect(() => {
    const chave = `${mesAno.getFullYear()}-${mesAno.getMonth() + 1}`;
    if (ultimoMesNotificado.current === chave) return;
    ultimoMesNotificado.current = chave;
    onMesChange?.(mesAno.getFullYear(), mesAno.getMonth() + 1);
  }, [mesAno, onMesChange]);

  // Dias úteis do mês corrente, até hoje, sem RDO preenchido e sem feriado/compensado
  const diasNaoPreenchidos = useMemo(() => {
    const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
    const anoAtual = hoje.getFullYear();
    const mesAtual = hoje.getMonth();

    if (mesAno.getFullYear() !== anoAtual || mesAno.getMonth() !== mesAtual) return [];

    const dias = [];
    for (let dia = 1; dia <= hoje.getDate(); dia++) {
      const dataObj = new Date(anoAtual, mesAtual, dia);
      const diaSemana = dataObj.getDay();
      const ehDiaUtil = diaSemana !== 0 && diaSemana !== 6;
      const dataStr = `${anoAtual}-${String(mesAtual + 1).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
      if (
        ehDiaUtil &&
        !diasPreenchidos.has(dataStr) &&
        !diasEspeciais.has(dataStr)
      ) {
        dias.push(dia);
      }
    }
    return dias;
  }, [diasPreenchidos, diasEspeciais, mesAno]);

  const carregarDias = async () => {
    setCarregandoCalendario(true); // NOVO
    try {
      const inicio = new Date(mesAno.getFullYear(), mesAno.getMonth(), 1);
      const fim = new Date(mesAno.getFullYear(), mesAno.getMonth() + 1, 0);

      const { data: notasMes, error } = await supabase
        .from("notas")
        .select("id, data_entrega")
        .eq("pilha_id", pilhaId)
        .eq("tipo", "Diário de Obra")
        .gte("data_entrega", inicio.toISOString().split("T")[0])
        .lte("data_entrega", fim.toISOString().split("T")[0]);

      if (error) { console.error("Erro ao carregar RDOs:", error); return; }

      setNotasDoMes(new Map((notasMes || []).map(n => [n.data_entrega, n.id])));

      const idsNotas = (notasMes || []).map(n => n.id);
      if (!idsNotas.length) {
        setDiasPreenchidos(new Set());
        setDiasEspeciais(new Set());
        return;
      }

      const { data: rdosMes, error: erroRdos } = await supabase
        .from("rdos")
        .select("nota_id, feriado, dia_compensado")
        .in("nota_id", idsNotas);

      if (erroRdos) { console.error("Erro ao carregar RDOs preenchidos:", erroRdos); return; }

      const idsComRdo = new Set((rdosMes || []).map(r => r.nota_id));
      setDiasPreenchidos(new Set(
        (notasMes || []).filter(n => idsComRdo.has(n.id)).map(n => n.data_entrega)
      ));

      const idsEspeciais = new Set(
        (rdosMes || []).filter(r => r.feriado || r.dia_compensado).map(r => r.nota_id)
      );
      setDiasEspeciais(new Set(
        (notasMes || []).filter(n => idsEspeciais.has(n.id)).map(n => n.data_entrega)
      ));
    } finally {
      setCarregandoCalendario(false); // NOVO
    }
  };

  useEffect(() => { carregarDias(); }, [pilhaId, mesAno]);

  // Reage ao evento global "rdoSalvo" disparado pelo modal do RDO
  useEffect(() => {
    const handleRdoSalvo = ({ detail }) => {
      if (!detail?.dataStr) return;
      const [ano, mes] = detail.dataStr.split("-").map(Number);
      if (ano !== mesAno.getFullYear() || mes - 1 !== mesAno.getMonth()) return;
      setDiasPreenchidos(prev => new Set(prev).add(detail.dataStr));
      if (detail.feriado || detail.dia_compensado) {
        setDiasEspeciais(prev => new Set(prev).add(detail.dataStr));
      } else {
        setDiasEspeciais(prev => {
          const cp = new Set(prev);
          cp.delete(detail.dataStr);
          return cp;
        });
      }
    };
    window.addEventListener("rdoSalvo", handleRdoSalvo);
    return () => window.removeEventListener("rdoSalvo", handleRdoSalvo);
  }, [mesAno]);

  const gerarDiasDoMes = () => {
    const ano = mesAno.getFullYear();
    const mes = mesAno.getMonth();
    const diasNoMes = new Date(ano, mes + 1, 0).getDate();
    const primeiroDiaSemana = new Date(ano, mes, 1).getDay();
    const limiteFuturo = getDataLimiteFutura();

    const dias = [];
    for (let i = 0; i < primeiroDiaSemana; i++) dias.push(null);
    for (let dia = 1; dia <= diasNoMes; dia++) {
      const dataStr = `${ano}-${String(mes + 1).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
      dias.push({
        dia,
        dataStr,
        preenchido: diasPreenchidos.has(dataStr),
        especial: diasEspeciais.has(dataStr),
        bloqueadoFuturo: dataStr > limiteFuturo && !notasDoMes.has(dataStr),
      });
    }
    return dias;
  };

  const handleDiaClick = async (dataStr) => {
    if (!dataStr || criandoNota) return;

    const limiteFuturo = getDataLimiteFutura();
    if (dataStr > limiteFuturo && !notasDoMes.has(dataStr)) {
      alert("Só é possível iniciar o Diário de Obra de hoje ou de amanhã.");
      return;
    }

    if (notasDoMes.has(dataStr)) {
      onSelectNota(notasDoMes.get(dataStr));
      return;
    }

    try {
      setCriandoNota(true);
      const dataObj = new Date(`${dataStr}T00:00:00`);
      const nomeFormatado = dataObj.toLocaleDateString('pt-BR');

      const { data: nova, error: erroInsert } = await supabase
        .from("notas")
        .insert([{
          nome: `RDO - ${nomeFormatado}`,
          tipo: "Diário de Obra",
          pilha_id: pilhaId,
          data_entrega: dataStr,
          responsavel: usuarioId,
          concluida: false,
          ordem: 0
        }])
        .select()
        .single();

      if (erroInsert || !nova) {
        alert("Erro ao criar o Diário de Obra: " + (erroInsert?.message || "Falha desconhecida"));
        return;
      }

      setNotasDoMes(prev => new Map(prev).set(dataStr, nova.id));
      onSelectNota(nova.id);
    } catch (err) {
      console.error("Erro inesperado em handleDiaClick:", err);
      alert("Ocorreu um erro inesperado. Tente novamente.");
    } finally {
      setCriandoNota(false);
    }
  };

  const dias = gerarDiasDoMes();
  const diasSemana = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

  // NOVO: mostra skeleton enquanto carrega
  if (carregandoCalendario) {
    return <RdoCalendarioSkeleton />;
  }

  return (
    <div className="calendario-diario-obras">
     

      <div className="calendario-header">
        <button onClick={() => setMesAno(new Date(mesAno.getFullYear(), mesAno.getMonth() - 1, 1))}>
          &lt;
        </button>
        <span>{mesAno.toLocaleString('pt-BR', { month: 'long', year: 'numeric' })}</span>
        <button onClick={() => setMesAno(new Date(mesAno.getFullYear(), mesAno.getMonth() + 1, 1))}>
          &gt;
        </button>
      </div>

      <div className="calendario-semana-header">
        {diasSemana.map(dia => (
          <div key={dia} className="calendario-dia-semana">
            {dia}
          </div>
        ))}
      </div>

      <div className="calendario-grid">
        {dias.map((item, index) => {
          const bloqueadoSemRdo = item?.bloqueadoFuturo && !item?.preenchido;
          return (
            <div
              key={index}
              className={`calendario-dia ${item ? (item.preenchido ? 'preenchido' : '') : 'vazio'} ${item?.especial ? 'especial' : ''} ${criandoNota ? 'disabled' : ''} ${bloqueadoSemRdo ? 'futuro-bloqueado' : ''}`}
              onClick={() => item && !bloqueadoSemRdo && handleDiaClick(item.dataStr)}
              style={{ cursor: criandoNota ? 'wait' : (bloqueadoSemRdo ? 'not-allowed' : (item ? 'pointer' : 'default')) }}
              title={bloqueadoSemRdo ? "Disponível apenas a partir de amanhã até hoje" : undefined}
            >
              {item ? item.dia : ''}
            </div>
          );
        })}
      </div>

       {diasNaoPreenchidos.length > 0 && (
        <div className="rdo-alerta-pendencia">
          <span className="rdo-alerta-pendencia1">
            {diasNaoPreenchidos.length} DIA{diasNaoPreenchidos.length > 1 ? "S" : ""} NÃO PREENCHIDO{diasNaoPreenchidos.length > 1 ? "S" : ""} </span><span className="rdo-alerta-pendencia2">{diasNaoPreenchidos.join(", ")}
          </span>
        </div>
      )}

      {criandoNota && (
        <div style={{
          textAlign: 'center',
          marginTop: '8px',
          fontSize: '0.85em',
          color: '#666'
        }}>
          Criando RDO...
        </div>
      )}
    </div>
  );
}